import { isEmpty, isEqual, xorWith } from 'lodash';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useGetPaginatedLocations, useParamQuery } from 'src/hooks';
import { ILocationListing } from 'src/interfaces';

export const useAddLocations = (
  locationGroupId: string,
  channelName: string,
) => {
  const { onChangeParams, getParamsValue, activeFilters } = useParamQuery({
    page: ['1'],
  });
  const pageParam = Number(getParamsValue('page')?.[0] || 1);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const activeFiltersArray = Object.entries(activeFilters).flatMap(
    ([key, values]) => values.map((value) => `${key}=${value}`),
  );
  const {
    data: paginatedLocations = { locations: [], totalCount: 0, totalPages: 0 },
    isLoading: isPaginatedLocationsLoading,
  } = useGetPaginatedLocations(
    [
      'sort=-created_at,location_code',
      `location_group_id.notlist=${locationGroupId}`,
      `channel_name.list=${channelName}`,
      `unassigned_group_channel_name=${channelName}`,
      `page=${page}`,
      ...activeFiltersArray,
    ],
    undefined,
    {
      enabled: Boolean(channelName && locationGroupId),
    },
  );

  const locations = paginatedLocations?.locations ?? [];
  const totalCount = paginatedLocations?.totalCount ?? 0;
  const totalPages = paginatedLocations?.totalPages ?? 0;

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    onChangeParams('page', [`${value}`]);
  };

  const [selectedLocations, setSelectedLocations] = useState<
    ILocationListing[]
  >([]);

  const formMethods = useForm<{
    locations: ILocationListing[];
  }>({
    values: {
      locations,
    },
  });

  const { locations: currentLocations } = formMethods.getValues();

  const onChangedSelectedLocations = (
    location: ILocationListing | ILocationListing[],
  ) => {
    const locationsArray = Array.isArray(location) ? location : [location];

    const updatedSelection = xorWith(
      selectedLocations,
      locationsArray,
      isEqual,
    );
    setSelectedLocations(updatedSelection);
  };

  const canSave = !isEmpty(selectedLocations);

  const onClickSelectAll = () => {
    const allSelected = currentLocations.every((loc) =>
      selectedLocations.some((sel) => sel.location_code === loc.location_code),
    );

    if (allSelected) {
      setSelectedLocations((prev) =>
        prev.filter(
          (sel) =>
            !currentLocations.some(
              (loc) => loc.location_code === sel.location_code,
            ),
        ),
      );
    } else {
      const merged = [...selectedLocations];
      currentLocations.forEach((loc) => {
        if (!merged.some((sel) => sel.location_code === loc.location_code)) {
          merged.push(loc);
        }
      });
      setSelectedLocations(merged);
    }
  };

  return {
    canSave,
    formMethods,
    isPaginatedLocationsLoading,
    onChangedSelectedLocations,
    totalCount,
    totalPages,
    handlePageChange,
    page,
    onClickSelectAll,
    selectedLocations,
    activeFilters,
    pageSize: totalPages > 0 ? Math.ceil(totalCount / totalPages) : 0,
  };
};
